import React from 'react';
import { AssessmentSidebar } from './assessment-sidebar';
import { AssessmentContent } from './assessment-content';
import { SharedAssessmentData, SRSPropertyType } from '@/lib/shared-data';
import { Property } from '@/store/properties-slice';

interface AssessmentMainContentProps {
  selectedDepartmentId: string | null;
  selectedTouchpointId: string | null;
  isSidebarOpen: boolean;
  onDepartmentClick: (departmentId: string) => void;
  onTouchpointClick: (touchpointId: string) => void;
  onSidebarToggle: () => void;
  assessmentData: SharedAssessmentData;
  touchpointProgress: Array<{
    touchpointId: string;
    totalStandards: number;
    answeredStandards: number;
  }>;
  overallCompletionPercentage: number;
  currentTouchpoint: { name: string } | null;
  onStandardStatusChange: (
    titleId: number,
    standardId: number,
    status: 'yes' | 'no' | 'na'
  ) => void;
  onStandardNotesChange: (
    titleId: number,
    standardId: number,
    notes: string
  ) => void;
  onTouchpointTimeChange: (answeredAt: string) => void;
  onMediaUpload: (type: 'photo' | 'audio') => void;
  touchpointAnsweredAt?: string;
  propertyData: SRSPropertyType & { actualProperty?: Property };
  isLoadingTouchpoint?: boolean;
  currentImagesCount?: number;
  currentAudiosCount?: number;
  maxImages?: number;
  maxAudios?: number;
  onMediaIconClick?: () => void;
  isReadOnly?: boolean;
}

export function AssessmentMainContent({
  selectedDepartmentId,
  selectedTouchpointId,
  isSidebarOpen,
  onDepartmentClick,
  onTouchpointClick,
  onSidebarToggle,
  assessmentData,
  touchpointProgress,
  overallCompletionPercentage,
  currentTouchpoint,
  onStandardStatusChange,
  onStandardNotesChange,
  onTouchpointTimeChange,
  onMediaUpload,
  touchpointAnsweredAt,
  propertyData,
  isLoadingTouchpoint = false,
  currentImagesCount = 0,
  currentAudiosCount = 0,
  maxImages = 10,
  maxAudios = 5,
  onMediaIconClick,
  isReadOnly = false,
}: AssessmentMainContentProps) {
  return (
    <div className="flex flex-1 relative">
      {/* Assessment Sidebar - Desktop: fixed, Mobile: overlay */}
      <AssessmentSidebar
        propertyData={propertyData}
        selectedDepartmentId={selectedDepartmentId}
        selectedTouchpointId={selectedTouchpointId}
        onDepartmentClick={onDepartmentClick}
        onTouchpointClick={onTouchpointClick}
        touchpointProgress={touchpointProgress}
        overallCompletionPercentage={overallCompletionPercentage}
        isOpen={isSidebarOpen}
        onToggle={onSidebarToggle}
        className="lg:flex-shrink-0"
      />

      {/* Assessment Content - Full width on mobile, with sidebar space on desktop */}
      <AssessmentContent
        selectedTouchpointName={currentTouchpoint?.name || ''}
        titles={assessmentData.titles}
        onStandardStatusChange={onStandardStatusChange}
        onStandardNotesChange={onStandardNotesChange}
        onTouchpointTimeChange={onTouchpointTimeChange}
        onMediaUpload={onMediaUpload}
        touchpointAnsweredAt={touchpointAnsweredAt}
        className="flex-1 min-w-0 lg:ml-0"
        isLoading={isLoadingTouchpoint}
        currentImagesCount={currentImagesCount}
        currentAudiosCount={currentAudiosCount}
        maxImages={maxImages}
        maxAudios={maxAudios}
        departmentId={
          selectedDepartmentId ? parseInt(selectedDepartmentId) : undefined
        }
        touchpointId={
          selectedTouchpointId ? parseInt(selectedTouchpointId) : undefined
        }
        onMediaIconClick={onMediaIconClick}
        isReadOnly={isReadOnly}
      />
    </div>
  );
}
